import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { Building2, MessageCircle, Compass, FolderKanban, MapPin, Menu, X, ChevronRight, Tag, BookOpen } from "lucide-react";
import { useAuth } from "@/lib/auth";

const PUBLIC_NAV = [
  { to: "/chat", label: "AI-rådgivare", icon: MessageCircle, testid: "pub-nav-chat" },
  { to: "/guide", label: "Bygglovsguide", icon: Compass, testid: "pub-nav-guide" },
  { to: "/bygglov", label: "Regelbanken", icon: BookOpen, testid: "pub-nav-rulebank" },
  { to: "/municipality", label: "Hitta kommunen", icon: MapPin, testid: "pub-nav-municipality" },
];

export default function PublicLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isLanding = location.pathname === "/";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="pub-logo" className="flex items-center gap-2.5">
            <img src="/logo-icon.png" alt="Bygglovskompassen" className="w-10 h-10 object-contain" />
            <div className="leading-tight">
              <div className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">AI-driven e-tjänst</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {PUBLIC_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition ${
                    isActive ? "bg-blue-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                <n.icon className="w-4 h-4 stroke-[1.5]" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <button
                onClick={() => nav(user.role === "admin" ? "/dashboard" : "/projects")}
                data-testid="pub-go-app"
                className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-sm text-sm font-medium"
              >
                Till min sida <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="pub-login"
                  className="px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-sm">
                  Logga in
                </Link>
                <Link to="/register" data-testid="pub-register"
                  className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium">
                  Skapa konto
                </Link>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 rounded-sm hover:bg-zinc-100"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              {PUBLIC_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                      isActive ? "bg-blue-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-3 border-t border-zinc-100 flex gap-2">
                {user ? (
                  <button onClick={() => { setOpen(false); nav(user.role === "admin" ? "/dashboard" : "/projects"); }}
                    className="flex-1 bg-zinc-900 text-white px-4 py-2 rounded-sm text-sm font-medium">
                    Till min sida
                  </button>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)}
                      className="flex-1 text-center border border-zinc-300 px-4 py-2 rounded-sm text-sm font-medium">
                      Logga in
                    </Link>
                    <Link to="/register" onClick={() => setOpen(false)}
                      className="flex-1 text-center bg-blue-700 text-white px-4 py-2 rounded-sm text-sm font-medium">
                      Skapa konto
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className={isLanding ? "" : "flex-1"}>
        <Outlet />
      </main>

      <footer className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-semibold text-zinc-900 mb-3" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</div>
            <p className="text-zinc-500">Idé till färdigt bygglovsunderlag – digitalt och med AI-stöd.</p>
          </div>
          <div>
            <div className="font-semibold text-zinc-900 mb-3">Tjänsten</div>
            <ul className="space-y-2 text-zinc-600">
              <li><Link to="/chat" className="hover:text-blue-700">AI-rådgivare</Link></li>
              <li><Link to="/guide" className="hover:text-blue-700">Bygglovsguide</Link></li>
              <li><Link to="/municipality" className="hover:text-blue-700">Hitta kommunen</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-zinc-900 mb-3">Verktyg</div>
            <ul className="space-y-2 text-zinc-600">
              <li><Link to="/guide/permit" className="hover:text-blue-700">Behöver jag bygglov?</Link></li>
              <li><Link to="/guide/property" className="hover:text-blue-700">Fastighetsanalys</Link></li>
              <li><Link to="/guide/templates" className="hover:text-blue-700">Handlingsmallar</Link></li>
              <li><Link to="/guide/review" className="hover:text-blue-700">Ritningsgranskning</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-zinc-900 mb-3">Om oss</div>
            <ul className="space-y-2 text-zinc-600">
              <li><Link to="/terms" className="hover:text-blue-700" data-testid="footer-terms">Användarvillkor</Link></li>
              <li><Link to="/privacy" className="hover:text-blue-700" data-testid="footer-privacy">Integritetspolicy</Link></li>
              <li><Link to="/for-kommuner" className="hover:text-blue-700" data-testid="footer-for-kommuner">För kommuner</Link></li>
              <li><Link to="/blogg" className="hover:text-blue-700" data-testid="footer-blogg">Artiklar</Link></li>
              <li><Link to="/anmal-tillsyn" className="hover:text-blue-700">Anmäl överträdelse</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-200 py-4 text-center text-xs text-zinc-500">
          © 2026 Bygglovskompassen · Allt material är vägledande – kommunens beslut är alltid styrande.
        </div>
      </footer>
    </div>
  );
}
